import styled, { keyframes } from "styled-components";
import { mobileAuthSideContainer, mobileAuthMainContainer } from "../../../styles/authShellMixins";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Container principal
export const MainContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  min-height: 100vh;
  height: 100%;
  background: var(--app-bg-gradient);
  background-size: 400% 400%;
  font-family: "Inter", "Helvetica Neue", sans-serif;
  overflow-x: hidden;

  ${mobileAuthMainContainer}
`;

// Container lateral — scroll da página (tabela cresce com o conteúdo)
export const SideContainer = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable;
  position: relative;
  height: 100vh;
  min-height: 100vh;
  width: ${({ collapsed }) => (collapsed ? "calc(100% - 6vw)" : "calc(100% - 15vw)")};
  margin: 0;
  transition: margin-left 0.5s ease-in-out, width 0.5s ease-in-out;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: var(--scrollbar-track, transparent);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--accent, #6b8f71);
    border-radius: 10px;
  }

  ${mobileAuthSideContainer}
`;

// Cabeçalho
export const StyledButtonSpan = styled.span`
 color: var(--text-primary);
  font-weight: bold;
  text-decoration: underline;
  cursor: pointer;
`;

export const StyledSpan = styled.span`
  color: var(--text-primary);
  font-weight: bold;
`;

export const ResolutionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${({ status }) =>
    status === "created" ? "var(--accent)" : status === "rejected" ? "var(--danger)" : "var(--warning)"};
  background: ${({ status }) =>
    status === "created"
      ? "var(--accent-soft)"
      : status === "rejected"
      ? "color-mix(in srgb, var(--danger) 22%, transparent)"
      : "color-mix(in srgb, var(--warning) 20%, transparent)"};
  border: 1px solid
    ${({ status }) =>
      status === "created"
        ? "var(--accent-soft)"
        : status === "rejected"
        ? "color-mix(in srgb, var(--danger) 22%, transparent)"
        : "color-mix(in srgb, var(--warning) 35%, transparent)"};
`;

/** Badge "Criado" clicável — mesmo visual, comportamento de botão. */
export const ResolutionBadgeButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  color: var(--accent);
  background: var(--accent-soft);
  border: 1px solid var(--accent);
  transition: transform 0.15s ease, opacity 0.15s ease, box-shadow 0.15s ease;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
    opacity: 0.95;
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

export const ActionsCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
  justify-content: center;
`;

export const ActionButton = styled.button`
  border: 1px solid
    ${({ variant }) => (variant === "reject" ? "color-mix(in srgb, var(--danger) 22%, transparent)" : "var(--accent)")};
  background: ${({ variant }) => (variant === "reject" ? "color-mix(in srgb, var(--danger) 22%, transparent)" : "var(--accent-soft)")};
  color: ${({ variant }) => (variant === "reject" ? "var(--danger)" : "var(--success)")};
  border-radius: 10px;
  padding: 6px 10px;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  white-space: nowrap;
  width: 100%;
  box-sizing: border-box;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    opacity: 0.95;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/** Área principal: largura total; conteúdo cresce e o scroll fica na página */
export const PageContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: none;
  box-sizing: border-box;
  padding: 0.75rem 1.25rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  align-self: stretch;
  flex: 0 0 auto;
  height: auto;
  min-height: min-content;
  overflow: visible;

  @media (max-width: 768px) {
    padding: 0.5rem 0.5rem 2rem;
  }
`;

export const Header = styled.div`
  text-align: left;
  margin-bottom: 1rem;
  position: relative;
  width: 100%;
`;

export const Title = styled.h2`
  font-size: 1.65rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--accent-secondary) 0%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0.5rem 0 0.25rem 0;
  text-shadow: 0 4px 20px color-mix(in srgb, var(--accent-secondary) 30%, transparent);
  text-align: left;
  animation: ${fadeInUp} 0.6s ease-out;
  animation-delay: 0.1s;
  animation-fill-mode: both;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.35rem;
  }
`;

export const EmptyMessage = styled.div`
  padding: 24px 1.25rem;
  text-align: left;
  color: var(--text-secondary);
  font-size: 0.95rem;
`;

export const StyledTable = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  backdrop-filter: blur(20px);
  background: var(--surface-2);
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid var(--border-subtle);
`;

export const StyledTableHead = styled.thead`
  background: var(--table-header-bg);
`;

export const StyledTableRow = styled.tr`
  border-bottom: 1px solid var(--border-subtle);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  color: var(--text-primary);
  height: 100%;
  width: 100%;

  &:last-child {
    border-bottom: none;
  }
`;

export const StyledTableCell = styled.td`
  padding: 10px 12px;
  vertical-align: top;
  color: var(--text-primary);
  font-size: 0.8125rem;
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: anywhere;

  &:nth-child(1) {
    text-align: center;
    width: 6.5%;
  }
  &:nth-child(2) {
    text-align: left;
    width: 21%;
  }
  &:nth-child(3) {
    text-align: left;
    width: 12%;
  }
  &:nth-child(4) {
    text-align: center;
    width: 10%;
    white-space: nowrap;
  }
  &:nth-child(5) {
    text-align: left;
    width: 11%;
  }
  &:nth-child(6) {
    text-align: center;
    width: 8%;
  }
  &:nth-child(7) {
    text-align: center;
    width: 7.5%;
    vertical-align: middle;
  }
  &:nth-child(8) {
    text-align: center;
    width: 7%;
    vertical-align: middle;
  }
  &:nth-child(9) {
    text-align: left;
    width: 17%;
    min-width: 200px;
    overflow-wrap: normal;
    word-break: normal;
    vertical-align: middle;
  }
`;

export const StyledTableHeader = styled.th`
  padding: 11px 12px;
  font-weight: var(--table-header-font-weight);
  color: var(--table-header-color);
  font-size: var(--table-header-font-size);
  text-transform: uppercase;
  letter-spacing: var(--table-header-letter-spacing);
  backdrop-filter: blur(10px);
  vertical-align: bottom;
  line-height: 1.25;
  word-wrap: break-word;

  &:nth-child(1) {
    text-align: center;
    width: 6.5%;
  }
  &:nth-child(2) {
    text-align: left;
    width: 21%;
  }
  &:nth-child(3) {
    text-align: left;
    width: 12%;
  }
  &:nth-child(4) {
    text-align: center;
    width: 10%;
  }
  &:nth-child(5) {
    text-align: left;
    width: 11%;
  }
  &:nth-child(6) {
    text-align: center;
    width: 8%;
  }
  &:nth-child(7) {
    text-align: center;
    width: 7.5%;
  }
  &:nth-child(8) {
    text-align: center;
    width: 7%;
  }
  &:nth-child(9) {
    text-align: left;
    width: 17%;
    min-width: 200px;
  }
`;

// Botão estilizado
export const StyledButton = styled.button`
  position: relative;
  background: var(--accent-soft);
  backdrop-filter: blur(20px);
  border: 2px solid var(--accent-soft);
  border-radius: 50px;
  //padding: 1rem 3rem;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  color: var(--accent-secondary);
  font-weight: 600;
  font-size: 0.5rem;
  margin:  1rem 0 ;
  height: 32px;  
  min-width: 200px; 
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;


  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, var(--accent-secondary-soft) 0%, var(--accent-soft) 100%);
    opacity: 0;
    transition: opacity 0.4s ease;
    border-radius: 50px;
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 50px;
    background: transparent;
    box-shadow: 0 8px 25px var(--accent-soft);
    opacity: 0;
    transition: all 0.4s ease;
    z-index: -1;
  }

  &:hover::before {
    opacity: 1;
  }

  &:hover::after {
    opacity: 1;
  }

  &:hover {
    transform: translateY(-4px);
    border-color: var(--accent);
    color: var(--accent-secondary);
    box-shadow: inset 0 1px 0 var(--surface-glass-strong);
  }

  &:active {
    transform: translateY(-2px);
  }

  > * {
    position: relative;
    z-index: 2;
  }

  @media (max-width: 768px) {
    padding: 0.8rem 2rem;
    font-size: 0.9rem;
    min-width: 120px;
  }

  @media (max-width: 480px) {
    padding: 0.7rem 1.5rem;
    font-size: 0.8rem;
    min-width: 100px;
  }
`;

export const TableContainer = styled.div`
  width: 100%;
  max-width: none;
  flex: 0 0 auto;
  height: auto;
  min-height: min-content;
  max-height: none;
  overflow-x: auto;
  overflow-y: visible;
  margin: 0.5rem 0 0;
  align-self: stretch;
  background: var(--surface-glass);
  backdrop-filter: blur(20px);
  border: 1px solid var(--accent-secondary-soft);
  border-radius: 16px;
  padding: 1rem 1rem 1.25rem;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;

  /* Scrollbar horizontal, se necessário */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: var(--surface-overlay);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, var(--accent-secondary) 0%, var(--accent) 100%);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, var(--accent) 0%, var(--accent-secondary) 100%);
  }

  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

/* —— Diálogos (mesma linguagem visual da página / TableContainer) —— */

export const DialogOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: var(--surface-overlay);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  box-sizing: border-box;
`;

export const DialogPanel = styled.div`
  position: relative;
  width: 100%;
  max-width: 460px;
  background: var(--app-bg-gradient);
  background-size: 400% 400%;
  border: 2px solid var(--border-subtle);
  border-radius: 20px;
  padding: 1.6rem 1.5rem 1.4rem;
  box-shadow: var(--shadow-lg);
  animation: ${fadeInUp} 0.35s ease-out;
`;

export const FeedbackPanel = styled(DialogPanel)`
  border-color: ${({ $variant }) =>
    $variant === "success" ? "var(--accent)" : "color-mix(in srgb, var(--danger) 22%, transparent)"};
  max-width: 420px;
`;

export const DialogTitle = styled.h3`
  margin: 0 0 0.85rem 0;
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.3;
  background: linear-gradient(135deg, var(--accent-secondary) 0%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

export const DialogText = styled.p`
  margin: 0 0 1.35rem 0;
  color: var(--text-primary);
  font-size: 0.94rem;
  line-height: 1.55;

  strong {
    color: var(--accent-secondary);
    font-weight: 600;
  }
`;

export const DialogFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  justify-content: flex-end;
`;

export const DialogButton = styled.button`
  border-radius: 12px;
  padding: 10px 18px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.15s ease, border-color 0.2s ease;
  border: 2px solid transparent;
  min-width: 118px;

  ${({ $variant }) =>
    $variant === "ghost"
      ? `
    background: var(--surface-glass);
    border-color: var(--border-subtle);
    color: var(--text-primary);
    &:hover:not(:disabled) {
      background: var(--surface-glass-strong);
    }
  `
      : $variant === "danger"
      ? `
    background: color-mix(in srgb, var(--danger) 22%, transparent);
    border-color: color-mix(in srgb, var(--danger) 22%, transparent);
    color: var(--danger);
    &:hover:not(:disabled) {
      opacity: 0.95;
      transform: translateY(-1px);
    }
  `
      : `
    background: var(--accent-soft);
    border-color: var(--accent);
    color: var(--success);
    &:hover:not(:disabled) {
      opacity: 0.95;
      transform: translateY(-1px);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export const FeedbackIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  margin: 0 auto 1rem;
  border-radius: 50%;
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1;
  ${({ $variant }) =>
    $variant === "success"
      ? `
    background: var(--accent-soft);
    border: 1px solid var(--accent);
    color: var(--success);
  `
      : `
    background: color-mix(in srgb, var(--danger) 22%, transparent);
    border: 1px solid color-mix(in srgb, var(--danger) 45%, transparent);
    color: var(--danger);
  `}
`;

export const DesktopOnly = styled.div`
  width: 100%;

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

export const MobileCards = styled.div`
  display: none;

  @media screen and (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
  }
`;

export const MobileCard = styled.article`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.9rem 1rem;
  border-radius: 14px;
  border: 1px solid var(--border-default);
  background: var(--surface-2);
`;

export const MobileCardTitle = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-heading);
`;

export const MobileCardMeta = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-secondary);
  line-height: 1.4;
  word-break: break-word;
`;

export const MobileCardActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.35rem;

  button {
    width: 100%;
  }
`;

/** Wrapper do painel embutido (aba Guardian / view_alerts). */
export const PanelRoot = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1rem;
  box-sizing: border-box;
`;
